import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * Put a list back where the reader left it when they come Back to it.
 *
 * The office tree scrolls `<main>`, not the window (AppLayout), so the browser's
 * own restoration never applies there and nothing else saved it: `useListState`
 * read `window.scrollY`, which is always 0 on that tree. The field tree scrolls
 * the document, where the browser restores on its own but usually before the
 * list has rendered, so it lands short.
 *
 * Positions are keyed on the history entry (`location.key`), not the path, so
 * two visits to /jobs with different searches remember different places.
 *
 * - POP (Back/Forward): restore the saved position. The rows may still be
 *   arriving, so keep re-applying it each frame until it sticks, the reader
 *   scrolls, or ~1.5s passes.
 * - PUSH to a new path: start at the top. A detail screen opened from row 40
 *   used to open scrolled 40 rows down.
 * - REPLACE, or a PUSH that only changes the query: leave it. List state writes
 *   (useUrlState) replace the entry on every keystroke.
 */
type ScrollTarget = HTMLElement | Window;

const STORAGE_KEY = 'fieldiq:scroll-positions';
const MAX_ENTRIES = 50;
const RESTORE_WINDOW_MS = 1500;

const positions = new Map<string, number>(readStored());

function readStored(): Array<[string, number]> {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is [string, number] => Array.isArray(e) && typeof e[0] === 'string' && Number.isFinite(e[1]),
    );
  } catch {
    return [];
  }
}

function remember(key: string, top: number) {
  positions.delete(key);
  positions.set(key, top);
  while (positions.size > MAX_ENTRIES) positions.delete(positions.keys().next().value as string);
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(positions.entries())));
  } catch {
    /* private window or full storage: the in-memory map still covers this tab */
  }
}

function readTop(target: ScrollTarget): number {
  return target instanceof Window ? target.scrollY : target.scrollTop;
}

function writeTop(target: ScrollTarget, top: number) {
  if (target instanceof Window) target.scrollTo(0, top);
  else target.scrollTop = top;
}

/** For tests only. */
export function __resetScrollPositions() {
  positions.clear();
}

export function useScrollRestoration(getTarget: () => ScrollTarget | null) {
  const location = useLocation();
  const navigationType = useNavigationType();
  const keyRef = useRef(location.key);
  const pathRef = useRef(location.pathname);
  const getTargetRef = useRef(getTarget);
  getTargetRef.current = getTarget;

  // Record every scroll against the entry currently on screen.
  useEffect(() => {
    const target = getTargetRef.current();
    if (!target) return;
    const onScroll = () => remember(keyRef.current, readTop(target));
    target.addEventListener('scroll', onScroll, { passive: true });
    return () => target.removeEventListener('scroll', onScroll);
  }, []);

  useLayoutEffect(() => {
    const previousPath = pathRef.current;
    keyRef.current = location.key;
    pathRef.current = location.pathname;
    const target = getTargetRef.current();
    if (!target) return;

    if (navigationType === 'POP') {
      const saved = positions.get(location.key);
      if (saved === undefined) return;
      let frame = 0;
      let cancelled = false;
      const started = Date.now();
      const stop = () => { cancelled = true; cancelAnimationFrame(frame); detach(); };
      const events = ['wheel', 'touchstart', 'keydown', 'mousedown'] as const;
      const detach = () => events.forEach(e => target.removeEventListener(e, stop));
      events.forEach(e => target.addEventListener(e, stop, { passive: true, once: true }));
      const apply = () => {
        if (cancelled) return;
        writeTop(target, saved);
        if (Math.abs(readTop(target) - saved) <= 1 || Date.now() - started > RESTORE_WINDOW_MS) {
          detach();
          return;
        }
        frame = requestAnimationFrame(apply);
      };
      apply();
      return stop;
    }

    if (navigationType === 'PUSH' && location.pathname !== previousPath) writeTop(target, 0);
    return undefined;
  }, [location.key, location.pathname, navigationType]);
}
